/**
 * TREATX MFA Challenge Page
 * =========================
 * Second-factor challenge for an already-enrolled privileged
 * TreatX account. Reads the intermediate `mfa_challenge` token from
 * sessionStorage, submits it together with the 6-digit TOTP code (or
 * a single-use recovery code) to /api/mfa/challenge/complete. On
 * success the backend sets the final HttpOnly session cookies and
 * the caller is signed in with a fully privileged JWT.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { persistCanonicalSessionFromMfa } from '../utils/finaliseMfaSession';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MfaChallengePage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('totp');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mfaToken = (() => {
    try { return sessionStorage.getItem('treatx.mfa.token') || ''; } catch { return ''; }
  })();
  const accountEmail = (() => {
    try { return sessionStorage.getItem('treatx.mfa.email') || ''; } catch { return ''; }
  })();

  if (!mfaToken) {
    navigate('/login', { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const body = { mfa_token: mfaToken };
    if (mode === 'totp') {
      if (!/^\d{6}$/.test(code)) {
        setError('Enter the 6-digit code from your authenticator app');
        return;
      }
      body.code = code;
    } else {
      if (!recovery || recovery.trim().length < 8) {
        setError('Enter one of your recovery codes');
        return;
      }
      body.recovery_code = recovery.trim();
    }
    setLoading(true);
    try {
      // TREATX-PROD-UAT-MFA-CSRF-01 (2026-08): the MFA challenge is a
      // login-completion endpoint (semantically identical to
      // `/api/auth/unified/login`). It is authenticated by a short-
      // lived purpose-scoped `mfa_challenge` JWT — no final session
      // cookie yet. We do NOT send the browser's cookies on this
      // request (`withCredentials: false`) so a STALE `treatx_auth`
      // cookie left over from a previous session cannot cause the
      // CSRF middleware to classify this request as cookie-
      // authenticated and reject it with `CSRF_DENIED`. The backend
      // still verifies the token's `purpose` claim and only THEN
      // issues the final `Set-Cookie` (both HttpOnly `treatx_auth`
      // and readable `treatx_csrf`) via the response headers.
      // `Set-Cookie` on a same-origin response is honoured regardless
      // of `withCredentials`. The endpoint is also listed as PAB in
      // the canonical CSRF exempt table.
      const r = await axios.post(`${API}/mfa/challenge/complete`, body, {
        headers: { Authorization: `Bearer ${mfaToken}` },
        withCredentials: false,
      });
      try {
        sessionStorage.removeItem('treatx.mfa.token');
        sessionStorage.removeItem('treatx.mfa.email');
      } catch { /* noop */ }
      // Persist the canonical session snapshot from the response body
      // (identical shape to the login response) so AuthContext +
      // RBACContext are hydrated on reload and the SoftLaunchGate
      // does NOT replace the workspace with the public landing.
      let destination = '/';
      try {
        destination = persistCanonicalSessionFromMfa(r.data || null) || '/';
      } catch { /* noop — fall through to '/' */ }
      toast.success('Signed in');
      window.location.hash = destination;
      setTimeout(() => window.location.reload(), 50);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Verification failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
         data-testid="mfa-challenge-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow p-8">
        <h1 className="text-2xl font-semibold mb-2">Two-factor authentication</h1>
        {accountEmail && (
          <p className="text-xs text-slate-500 mb-4">Signed in as {accountEmail}</p>
        )}
        <p className="text-sm text-slate-600 mb-4">
          Enter the 6-digit code from your authenticator app.
        </p>
        <form onSubmit={submit}>
          {mode === 'totp' ? (
            <>
              <input
                data-testid="mfa-challenge-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                pattern="[0-9]*"
                autoFocus
                className="w-full text-2xl tracking-widest text-center border rounded-lg py-2 mb-4"
                placeholder="000000"
              />
              <button
                type="button"
                data-testid="mfa-use-recovery-btn"
                onClick={() => { setError(''); setMode('recovery'); }}
                className="text-xs text-slate-600 underline mb-4"
              >
                Use a recovery code instead
              </button>
            </>
          ) : (
            <>
              <input
                data-testid="mfa-challenge-recovery-input"
                value={recovery}
                onChange={(e) => setRecovery(e.target.value.toUpperCase())}
                autoFocus
                className="w-full font-mono text-center border rounded-lg py-2 mb-4"
                placeholder="XXXXX-XXXXX"
              />
              <button
                type="button"
                data-testid="mfa-use-totp-btn"
                onClick={() => { setError(''); setMode('totp'); }}
                className="text-xs text-slate-600 underline mb-4"
              >
                Back to authenticator code
              </button>
            </>
          )}
          {error && (
            <p className="text-sm text-red-600 mb-2" data-testid="mfa-challenge-error">{error}</p>
          )}
          <button
            data-testid="mfa-challenge-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-emerald-600 text-white py-2.5 font-medium disabled:bg-slate-300 hover:bg-emerald-700"
          >
            {loading ? 'Verifying…' : 'Verify'}
          </button>
        </form>
      </div>
    </div>
  );
}
